import React from "react";
import { Formik } from "formik";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { TextField, Grid, Box, FormHelperText, Button } from "@mui/material";
import * as Yup from "yup";
import PincodeAddressFields from "components/PincodeAddressFields";
import { updateAddress } from "actions/businessActions";

const validationSchema = Yup.object().shape({
  addressType: Yup.string().max(16).required("Address type is required."),
  addressLine1: Yup.string().max(160).required("Please enter Address Line 1."),
  addressLine2: Yup.string().max(160),
  area: Yup.string().max(120),
  city: Yup.string().max(80).required("Please enter city."),
  division: Yup.string().max(80),
  district: Yup.string().max(80),
  state: Yup.string().max(80).required("Please enter state."),
  country: Yup.string().max(80).required("Please enter country."),
  pincode: Yup.string().trim().matches(/^[1-9]\d{5}$/, "Please enter a valid 6-digit pincode.").required("Pincode is required."),
});

const BusinessAddressForm = ({ supplierId, address, handleClose, ...rest }) => {
  const dispatch = useDispatch();
  return (
    <Formik
      key={String(supplierId) + "-" + (address?._id || "new")}
      initialValues={{
        addressId: address?._id,
        addressType: address?.addressType || "HEAD OFFICE",
        addressLine1: address?.addressLine1 || "",
        addressLine2: address?.addressLine2 || "",
        area: address?.area || "",
        city: address?.city || "",
        division: address?.division || "",
        district: address?.district || "",
        state: address?.state || "",
        country: address?.country || "India",
        pincode: address?.pincode == null ? "" : String(address.pincode),
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await dispatch(updateAddress(supplierId, values));
          handleClose();
        } catch (error) {
          const message = typeof error === "string" ? error : error.response?.data?.message || error.message || "Unable to update address. Please try again.";
          setStatus({ success: false });
          setErrors({ submit: message });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
        <form onSubmit={handleSubmit} {...rest}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <PincodeAddressFields preserveSavedAddress={Boolean(address)} variant="marketplace" disabled={isSubmitting} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                name="addressLine1"
                value={values.addressLine1}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                inputProps={{ maxLength: 160 }}
                error={Boolean(touched.addressLine1 && errors.addressLine1)}
                helperText={touched.addressLine1 && errors.addressLine1}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                name="addressLine2"
                value={values.addressLine2}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                inputProps={{ maxLength: 160 }}
                error={Boolean(touched.addressLine2 && errors.addressLine2)}
                helperText={touched.addressLine2 && errors.addressLine2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                name="country"
                value={values.country}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                inputProps={{ maxLength: 80 }}
                error={Boolean(touched.country && errors.country)}
                helperText={touched.country && errors.country}
              />
            </Grid>
            {errors.submit && <Grid item xs={12}><Box><FormHelperText error>{errors.submit}</FormHelperText></Box></Grid>}
            <Grid item xs={12}>
              <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ backgroundColor: "black", color: "white", "&:hover": { backgroundColor: "black" } }}>
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

BusinessAddressForm.propTypes = {
  supplierId: PropTypes.number,
  address: PropTypes.object,
  handleClose: PropTypes.func,
};

export default BusinessAddressForm;
